import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { systemMessageService } from "../Services/systemMessageService";
import { generateRandomLetter, generateSpecialRounds } from "../Components/Utils/roomHelpers";
import analytics from "../Services/analyticsService";
import hostingService, { isNotDeployed } from "../Services/hostingService";
import useCoinStore from "../stores/coinStore";
import { ROOM_LIMIT_COST } from "../Services/roomLimits";
import { LIMIT_COPY } from "../utils/hostingCopy";

const useRoomData = () => {
  const [roomData, setRoomData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameStatus, setGameStatus] = useState("waiting");
  const [roomDocId, setRoomDocId] = useState(null);
  const [roomMaxPlayers, setRoomMaxPlayers] = useState(3);
  const [hostingLimit, setHostingLimit] = useState(null);
  const [startingGame, setStartingGame] = useState(false);
  const startInFlight = useRef(false);
  const [customTimeLimit, setCustomTimeLimit] = useState(45);
  const [hasCustomTimeLimit, setHasCustomTimeLimit] = useState(false);
  const [hasLetterPicker, setHasLetterPicker] = useState(false);
  const [customCategories, setCustomCategories] = useState([
    "name",
    "animal",
    "place",
    "thing",
  ]);

  const navigate = useNavigate();

  const currentUserId = localStorage.getItem("currentUserId");
  const isExplicitHost = localStorage.getItem("isHost") === "true";
  const isHostInRoom = roomData?.hostId === currentUserId;
  const isHost = isExplicitHost && isHostInRoom;

  useEffect(() => {
    const loadRoomData = async () => {
      try {
        const roomId = localStorage.getItem("currentRoomId");
        const isHost = localStorage.getItem("isHost") === "true";

        if (!roomId) {
          navigate("/");
          return;
        }

        let unsubscribe;

        try {
          const cachedDocId = sessionStorage.getItem(`docId_${roomId}`);

          if (cachedDocId) {
            unsubscribe = onSnapshot(
              doc(db, "rooms", cachedDocId),
              (docSnapshot) => {
                if (docSnapshot.exists()) {
                  const data = { id: docSnapshot.id, ...docSnapshot.data() };
                  handleRoomDataUpdate(data, isHost);
                  setRoomDocId(docSnapshot.id);
                } else {
                  handleRoomNotFound();
                }
                setLoading(false);
              },
              (error) => {
                console.error("Room listener error:", error);
                fallbackToQueryListener(roomId, isHost);
              }
            );
          } else {
            fallbackToQueryListener(roomId, isHost);
          }
        } catch (error) {
          console.error("Error setting up listener:", error);
          fallbackToQueryListener(roomId, isHost);
        }

        return () => {
          if (unsubscribe) unsubscribe();
        };
      } catch (error) {
        console.error("Error loading room data:", error);
        setLoading(false);
      }
    };

    const fallbackToQueryListener = (roomId, isHost) => {
      const q = query(collection(db, "rooms"), where("roomId", "==", roomId));

      const unsubscribe = onSnapshot(
        q,
        (querySnapshot) => {
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const data = { id: doc.id, ...doc.data() };

            sessionStorage.setItem(`docId_${roomId}`, doc.id);

            handleRoomDataUpdate(data, isHost);
            setRoomDocId(doc.id);
          } else {
            handleRoomNotFound();
          }
          setLoading(false);
        },
        (error) => {
          console.error("Fallback listener error:", error);
          setError("Connection issues. Please refresh the page.");
          setLoading(false);
        }
      );

      return unsubscribe;
    };

    const handleRoomDataUpdate = (data, isHost) => {
      const storedUserId = localStorage.getItem("currentUserId");
      const actualHostId = data.hostId;

      if (isHost && storedUserId !== actualHostId) {
        alert(
          "Your host session appears to be invalid. Please rejoin the room."
        );
        clearLocalStorage();
        navigate(`/join-room/${data.roomId}`);
        return;
      }

      setRoomData(data);
      setGameStatus(data.gameStatus || "waiting");
      setRoomMaxPlayers(data.maxPlayers || 3);
    };

    const handleRoomNotFound = () => {
      setError("Room not found or has been deleted.");
      clearLocalStorage();
      setLoading(false);
    };

    const clearLocalStorage = () => {
      localStorage.removeItem("currentRoomId");
      localStorage.removeItem("currentUserId");
      localStorage.removeItem("playerName");
      localStorage.removeItem("isHost");
    };

    loadRoomData();
  }, [navigate]);

  useEffect(() => {
    // Mobile writes gameStatus: "starting" when a host starts a round and never
    // writes "playing" back to Firestore at all (it's a client-only phase name
    // there) — checking only "playing" here meant this never fired for a game
    // started from mobile, leaving the web client stuck on the lobby screen.
    if ((gameStatus === "starting" || gameStatus === "playing") && roomData) {
      navigate("/play-game");
    }
  }, [gameStatus, navigate, roomData]);

  const updateCustomTimeLimitValue = async (newTimeLimit) => {
    if (!isHost || !roomDocId) return;

    try {
      const roomRef = doc(db, "rooms", roomDocId);
      const updateData = { customTimeLimit: newTimeLimit, hasCustomTimeLimit: true };
      await updateDoc(roomRef, updateData);
      setCustomTimeLimit(newTimeLimit);
      console.log(`✅ Timer updated to ${newTimeLimit}s`);
    } catch (error) {
      console.error("❌ Failed to update timer:", error);
    }
  };

  const handleRoomUpgrade = async (newMaxPlayers, coinsCost) => {
    try {
      if (roomDocId) {
        const roomRef = doc(db, "rooms", roomDocId);
        await updateDoc(roomRef, {
          maxPlayers: newMaxPlayers,
          upgradedBy: currentUserId,
          upgradesCost: coinsCost,
          upgradedAt: new Date(),
        });

        console.log(`✅ Room upgraded to ${newMaxPlayers} players`);
      }

      setRoomMaxPlayers(newMaxPlayers);
    } catch (error) {
      console.error("Error updating room:", error);
      alert("Failed to update room. Please try again.");
    }
  };

  const leaveRoom = async () => {
    try {
      const currentPlayerName = localStorage.getItem("playerName");

      // Use cached roomDocId instead of querying
      if (!roomDocId) {
        console.error("No room document ID available");
        navigate("/");
        return;
      }

      const updatedPlayers = roomData.players.filter(
        (player) => player.id !== currentUserId
      );

      await updateDoc(doc(db, "rooms", roomDocId), {
        players: updatedPlayers,
      });

      if (currentPlayerName) {
        try {
          await systemMessageService.playerLeft(roomDocId, currentPlayerName);
        } catch (error) {
          console.warn("Failed to send leave message:", error);
        }
      }

      localStorage.removeItem("currentRoomId");
      localStorage.removeItem("currentUserId");
      localStorage.removeItem("playerName");

      navigate("/");
    } catch (error) {
      console.error("Error leaving room:", error);
      alert("Failed to leave room. Please try again.");
    }
  };

  const deleteRoom = async () => {
    if (!isHost) return;

    try {
      // Use cached roomDocId instead of querying
      if (!roomDocId) {
        console.error("No room document ID available");
        navigate("/");
        return;
      }

      await deleteDoc(doc(db, "rooms", roomDocId));

      localStorage.removeItem("currentRoomId");
      localStorage.removeItem("currentUserId");
      localStorage.removeItem("playerName");

      navigate("/");
    } catch (error) {
      console.error("Error deleting room:", error);
      alert("Failed to delete room. Please try again.");
    }
  };

  const removePlayer = async (playerId) => {
    try {
      if (!isHost || !roomData || !roomDocId) return;

      const playerToRemove = roomData.players.find((p) => p.id === playerId);
      const playerName = playerToRemove?.name;

      // Use cached roomDocId instead of querying
      const updatedPlayers = roomData.players.filter(
        (player) => player.id !== playerId
      );

      await updateDoc(doc(db, "rooms", roomDocId), {
        players: updatedPlayers,
      });

      if (playerName) {
        try {
          const hostName = localStorage.getItem("playerName");
          await systemMessageService.playerRemoved(
            roomDocId,
            playerName,
            hostName
          );
        } catch (error) {
          console.warn("Failed to send removal message:", error);
        }
      }

      console.log(`Player ${playerId} removed from room`);
    } catch (error) {
      console.error("Error removing player:", error);
      alert("Failed to remove player. Please try again.");
    }
  };

  const transferHost = async (newHostId) => {
    try {
      if (!isHost || !roomData || !roomDocId) return;

      const currentHostName = localStorage.getItem("playerName");
      const newHost = roomData.players.find((p) => p.id === newHostId);
      const newHostName = newHost?.name;

      // Use cached roomDocId instead of querying
      const updatedPlayers = roomData.players.map((player) => ({
        ...player,
        isHost: player.id === newHostId,
      }));

      await updateDoc(doc(db, "rooms", roomDocId), {
        hostId: newHostId,
        players: updatedPlayers,
      });

      if (currentHostName && newHostName) {
        try {
          await systemMessageService.hostTransferred(
            roomDocId,
            currentHostName,
            newHostName
          );
        } catch (error) {
          console.warn("Failed to send host transfer message:", error);
        }
      }

      console.log(`Host status transferred to ${newHostId}`);
    } catch (error) {
      console.error("Error transferring host status:", error);
      alert("Failed to transfer host status. Please try again.");
    }
  };

  const handleCategoriesChange = async (newCategories) => {
    try {
      // Use cached roomDocId instead of querying
      if (!roomDocId) {
        console.error("No room document ID available");
        return false;
      }

      const defaultCategories = ["name", "animal", "place", "thing"];
      const isCustom =
        JSON.stringify(newCategories.map((c) => c.toLowerCase())) !==
        JSON.stringify(defaultCategories);

      const hasExtraCategories = newCategories.length > 4;
      const extraCategoriesCount = Math.max(0, newCategories.length - 4);

      await updateDoc(doc(db, "rooms", roomDocId), {
        categories: newCategories,
        useCustomCategories: isCustom,
        hasExtraCategories: hasExtraCategories,
        extraCategoriesCount: extraCategoriesCount,
        categoriesUpdatedAt: new Date(),
      });

      try {
        await systemMessageService.roomSettingsChanged(
          roomDocId,
          "categories",
          newCategories
        );
      } catch (error) {
        console.warn("Failed to send category update message:", error);
      }

      setCustomCategories(newCategories);

      console.log(
        `✅ Categories updated: ${newCategories.length} total (${extraCategoriesCount} extra)`
      );
      return true;
    } catch (error) {
      console.error("Error updating categories:", error);
      alert("Failed to update categories. Please try again.");
      return false;
    }
  };

  const failStart = (reason) => {
    analytics.track("game_start_failed", { room_id: roomDocId || null, reason });
  };

  // Takes a free hosting slot (or the coin payment) before the start is written.
  // Returns the authorization, or null when the game must not start.
  const authorizeStart = async (payWithCoins) => {
    let authorization = null;
    try {
      authorization = await hostingService.authorizeStart(roomDocId, { payWithCoins });
    } catch (err) {
      if (isNotDeployed(err)) return { ok: true, method: "free" };
      console.error("Error authorizing game start:", err);
      failStart(err.code || "authorize_failed");
      if (payWithCoins) {
        analytics.track("room_limit_bypass_failed", { cost: ROOM_LIMIT_COST, reason: err.code || "request_failed" });
        setHostingLimit((prev) => prev && { ...prev, error: LIMIT_COPY.hostNowError });
      } else {
        alert("Failed to start game. Please try again.");
      }
      return null;
    }
    if (authorization.ok) return authorization;

    if (authorization.reason === "limit_reached" || authorization.reason === "insufficient_coins") {
      if (payWithCoins) {
        analytics.track("room_limit_bypass_failed", { cost: authorization.cost, reason: authorization.reason });
        useCoinStore.getState().syncBalance();
      } else {
        analytics.track("room_limit_hit", { free_rooms_used: authorization.used ?? 3 });
      }
      setHostingLimit({
        nextAvailableAt: authorization.nextAvailableAt,
        serverNow: authorization.serverNow,
        cost: authorization.cost,
        error: payWithCoins ? LIMIT_COPY.hostNowError : "",
      });
      return null;
    }
    failStart(authorization.reason || "not_authorized");
    alert("Failed to start game. Please try again.");
    return null;
  };

  const startGame = async ({ payWithCoins = false } = {}) => {
    if (startInFlight.current) return;
    startInFlight.current = true;
    setStartingGame(true);
    let authorization = null;
    try {
      // Use cached roomDocId instead of querying
      if (!roomDocId) {
        console.error("No room document ID available");
        return;
      }

      if (roomData.hostId !== currentUserId) {
        alert("Only the host can start the game!");
        return;
      }

      if (payWithCoins) analytics.track("room_limit_bypass_attempted", { cost: ROOM_LIMIT_COST });
      authorization = await authorizeStart(payWithCoins);
      if (!authorization) return;

      const firstLetter = generateRandomLetter([]);

      let letterPickerTurnOrder = [];

      if (roomData.hasLetterPicker) {
        letterPickerTurnOrder = roomData.players.map((player) => ({
          userId: player.id,
          playerName: player.name,
        }));

        console.log(
          `🎯 Letter picker turn order:`,
          letterPickerTurnOrder.map((p) => p.playerName)
        );
      }

      let roundTimeLimit = 45;

      if (roomData.hasCustomTimeLimit && roomData.customTimeLimit) {
        roundTimeLimit = roomData.customTimeLimit;
        console.log(`🕒 Using custom time limit: ${roundTimeLimit}s`);
      }

      const specialRounds = generateSpecialRounds(roomData.enableSpecialRounds);

      await updateDoc(doc(db, "rooms", roomDocId), {
        gameStatus: "playing",
        currentLetter: firstLetter,
        playedLetters: [firstLetter],
        currentRound: 1,
        specialRounds: specialRounds,
        ...(roomData.hasLetterPicker && {
          letterPickerTurnOrder: letterPickerTurnOrder,
          currentLetterPickerIndex: 0,
          nextLetterPickerIndex: 1 % letterPickerTurnOrder.length,
        }),
        rounds: {
          1: {
            letter: firstLetter,
            startedAt: new Date(),
            timeLimit: roundTimeLimit,
            submissions: [],
            isComplete: false,
            letterSource: roomData.hasLetterPicker
              ? "random_first"
              : "random",
          },
        },
      });

      if (specialRounds.length > 0) {
        console.log(`🎰 Special rounds generated for rounds:`, specialRounds);
      }

      setHostingLimit(null);
      if (authorization.method === "coins") {
        analytics.track("room_limit_bypassed", { cost: authorization.cost, method: "coins" });
        useCoinStore.getState().syncBalance();
      }
      analytics.track("game_started", {
        room_id: roomDocId,
        player_count: roomData.players.length,
        special_rounds_enabled: !!roomData.enableSpecialRounds,
        has_letter_picker: !!roomData.hasLetterPicker,
        rematch: !!roomData.rematchOf,
        hosting_method: authorization.method,
      });
      // Round 1 is never a special round — generateSpecialRounds only
      // assigns rounds 2+ (see Components/Utils/roomHelpers.js).
      analytics.track("round_started", {
        room_id: roomDocId,
        round_number: 1,
        letter: firstLetter,
        is_special_round: false,
      });
    } catch (error) {
      console.error("Error starting game:", error);
      failStart(error.code || error.message || "unknown");
      if (authorization && authorization.method !== "pro") {
        hostingService.releaseStart(roomDocId).catch((err) => console.error("Release failed:", err));
      }
      alert("Failed to start game. Please try again.");
    } finally {
      startInFlight.current = false;
      setStartingGame(false);
    }
  };

  const endGame = async () => {
    if (!isHost) return;

    // The screen asks first (CreateRoom's confirm dialog).
    try {
      // Use cached roomDocId instead of querying
      if (!roomDocId) {
        console.error("No room document ID available");
        return;
      }

      await updateDoc(doc(db, "rooms", roomDocId), {
        gameStatus: "ended",
        gameEndedAt: new Date(),
        gameEndedBy: currentUserId,
      });

      const gameStartedAt = roomData?.rounds?.[1]?.startedAt?.toDate?.() ?? null;
      analytics.track("game_completed", {
        room_id: roomDocId,
        total_rounds: roomData?.currentRound ?? 0,
        duration_sec: gameStartedAt ? Math.round((Date.now() - gameStartedAt.getTime()) / 1000) : 0,
        end_reason: "host_forced",
      });

      alert(
        "Game has been ended. Current round will complete but no new rounds will start."
      );
    } catch (error) {
      console.error("Error ending game:", error);
      alert("Failed to end game. Please try again.");
    }
  };

  return {
    roomData,
    loading,
    error,
    gameStatus,
    roomDocId,
    roomMaxPlayers,
    customTimeLimit,
    hasCustomTimeLimit,
    hasLetterPicker,
    customCategories,
    isHost,
    setCustomTimeLimit,
    setHasCustomTimeLimit,
    setHasLetterPicker,
    updateCustomTimeLimitValue,
    handleRoomUpgrade,
    leaveRoom,
    deleteRoom,
    removePlayer,
    transferHost,
    handleCategoriesChange,
    startGame,
    startingGame,
    hostingLimit,
    setHostingLimit,
    endGame,
  };
};

export default useRoomData;
